import moment from 'moment'
import { useState } from 'react'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useReselector } from '@/src/utils/sharedHooks'

import {
  Card,
  Icon,
  Text,
  Accordion,
  Separator,
  CardColors,
  Badge,
  Button,
} from '@/ui'
import { Tooltip } from './Tooltip'
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { selectUserTaxCalculationWithinActiveQuarter } from '@/selectors/userTaxCalculation.selectors'
import { TypographyStyles } from '@/styles'
// TODO: Migrate to V2
import { selectActiveQuarterlyTaxEstimateDetails } from '@/featuresV1/Admin/QuarterlyTaxEstimateDetails/quarterlyTaxEstimateDetails.selector'

export const QTETimeline = () => {
  const activeQuarterDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )

  const calculation = useReselector(selectUserTaxCalculationWithinActiveQuarter)
  const isSingleState = calculation?.isSingleState

  const {
    taxQuarter,
    clientNotifiedAt: notifiedAt,
    irsPaymentDueAt,
    calculationStartsAt,
    checklistLaunchDate,
  } = activeQuarterDetails ?? {}

  const [accordionValue, setAccordionValue] = useState<number | null>(0)

  const handleAccordionClick = (
    _e: unknown,
    data: { taxquarter: number | null }
  ) => {
    const { taxquarter } = data
    if (taxquarter === accordionValue) {
      setAccordionValue(null)
    } else {
      setAccordionValue(taxquarter)
    }
  }

  if (!activeQuarterDetails) {
    return null
  }
  return (
    <Card color={CardColors.LEVEL_1} className="w-full">
      <Accordion type="single" collapsible>
        <Accordion.Item value="timeline">
          <Accordion.Trigger asChild>
            <Button
              onClick={() =>
                handleAccordionClick(null, { taxquarter: accordionValue })
              }
              className="flex items-center justify-between w-full cursor-pointer"
            >
              <Text variant={TypographyStyles.H3}>Timeline</Text>
              <div className="flex items-center gap-2">
                <Tooltip
                  popup={{
                    title: 'Timeline',
                    body: 'This section helps you keep track of where you are in the quarterly tax process.',
                    link: 'https://support.joinheard.com/hc/en-us/sections/4416200689303-Quarterly-Taxes',
                  }}
                  labelComponent={
                    <Icon
                      icon={regular('question-circle')}
                      size="1x"
                      className="text-neutral-500"
                    />
                  }
                />
                <Icon
                  icon={accordionValue === 0 ? solid('minus') : solid('plus')}
                  size="1x"
                />
              </div>
            </Button>
          </Accordion.Trigger>
          <Accordion.Content>
            {/* Step 1 */}
            <div className="mt-4 space-y-2">
              <Badge variant="default" size="md">
                <Text variant={TypographyStyles.BODY_MD}>1</Text>
              </Badge>
              <Text variant={TypographyStyles.H3}>
                Between{' '}
                {moment
                  .utc(checklistLaunchDate)
                  .format(DATE_FORMATS.DISPLAY_LONG)}{' '}
                and{' '}
                {moment
                  .utc(calculationStartsAt)
                  .subtract(1, 'day')
                  .format(DATE_FORMATS.DISPLAY_LONG)}
              </Text>
              <Text>Complete your quarterly checklist</Text>
              <Separator />
            </div>

            {/* Step 2 */}
            <div className="space-y-2">
              <Badge variant="default" size="md">
                <Text variant={TypographyStyles.BODY_MD}>2</Text>
              </Badge>
              <Text variant={TypographyStyles.H3}>
                By {moment.utc(notifiedAt).format(DATE_FORMATS.DISPLAY_LONG)}
              </Text>
              <Text>Heard sends you your Q{taxQuarter} estimates</Text>
              <Text className="mt-2">
                {!isSingleState
                  ? '(if quarterly checklist was completed by deadline)'
                  : 'Note: Heard will send you an estimate within one hour of completing your checklist.'}
              </Text>
              <Separator />
            </div>

            {/* Step 3 */}
            <div className="space-y-2">
              <Badge variant="default" size="md">
                <Text variant={TypographyStyles.BODY_MD}>3</Text>
              </Badge>
              <Text variant={TypographyStyles.H3}>
                By{' '}
                {moment.utc(irsPaymentDueAt).format(DATE_FORMATS.DISPLAY_LONG)}
              </Text>
              <Text>
                Submit your quarterly tax payments to the IRS and{' '}
                {!isSingleState ? 'applicable states' : 'your home state'}.
              </Text>
            </div>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
    </Card>
  )
}
